/* eslint-disable import/no-named-as-default-member */
import _ from 'underscore';
import Promise from 'bluebird';
import edm from '@bingads-webui-component-legacy/edm-campaign/index';
import state from '@bingads-webui-component-legacy/state/index';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import dateUtil from '@bingads-webui-component-legacy/humanize/date';
import appConfig from '@bingads-webui-component-legacy/config/index';
import pageContext from '@bingads-webui-static-legacy/page-context';
import cacheService from '@bingads-webui-static-legacy/cache-service';
import sessionStore from '@bingads-webui-component-legacy/js-data-storage/session-storage';
import dataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';
import legacyPreferenceService from '@bingads-webui-component-legacy/preference/service/index';
import * as preferenceStoreFactory from '@bingads-webui-component-legacy/bingads-grid/service/filter-preferences';
import * as SegmentationPreference from '@bingads-webui-component-legacy/bingads-grid/service/segmentation-preferences';
import * as customColumnPreference from '@bingads-webui-component-legacy/bingads-grid/service/custom-column-preferences';
import * as ColumnChooserPreference from '@bingads-webui-component-legacy/bingads-grid/service/column-chooser-preferences';
import { AdGroup } from '@bingads-webui-campaign/campaign-ui-schemas';
import { fetchSortInfo } from '@bingads-webui-component-legacy/bingads-grid/service/fetch-sortinfo';
import { FilterService } from '@bingads-webui/filter-service';
import { getGridDataParams } from '@bingads-webui-campaign/summary-grid-helper';
import { fetchPaginationConfig } from '@bingads-webui-component-legacy/bingads-grid/service/fetch-pagination';
import { getColumnsConversionMap } from '@bingads-webui-campaign/column-conversions';
import { ChartDataAdapter } from '@bingads-webui-campaign-react/inline-chart';
import { InlineChartPreferences } from '@bingads-webui-campaign/inline-chart-preferences';
import { store, adapter } from '@bingads-webui-component-legacy/bingads-grid/service/grid-preferences';
import { DataSourceStore } from '@bingads-webui-campaign/data-source-store';
import { getFilterConfig } from './filter-config';
import { getColumnConfig } from './columns-config';

const gridName = 'summary_tab_ad_group';
const CurrentTabId = 'Adgroups';
const isOdataActionSource = true;
export function getDataSourceConfig({
  gridNameSource,
  scope,
  logActivity,
  preferenceService,
  i18n,
  ...restParams
}) {
  const Permissions = appConfig.get('Permissions');
  const { dynamic = {} } = Permissions;
  const {
    ColumnConversion: IsColumnConversionEnabled,
    AdgroupsCombineGridAndChartOdataCallEnabled,
  } = dynamic;

  const initialQuery = isOdataActionSource ? {
    query: {
      nullifynotapplybidamounts: true,
    },
    gridDataParameters: getGridDataParams(appConfig),
  } : {};

  const scopeAdapter = _.extend({
    customerId: scope.values.cid,
    accountId: scope.values.aid,
    campaignId: scope.values.campaignId,
    adGroupId: scope.values.adGroupId,
  }, scope);

  // The edm entity read campaign_id from state
  if (scopeAdapter.campaignId) {
    state.set('campaign_id', scopeAdapter.campaignId);
  } else {
    state.unset('campaign_id');
  }

  const edmEntity = scopeAdapter.campaignId ? edm.currentCampaign : edm.currentAccount;
  const entitySet = edmEntity.AdGroups['Default.GridData'];

  let existingDataSource;
  let filterService;
  let filterConfig;
  let dataSource = {
    type: 'jsdata',
    entity: jsdata.AdGroup,
    primaryKey: 'Id',
    options: {
      entitySet: edmEntity.AdGroups,
      useDfmCache: false,
      cacheResponse: false,
    },
  };
  const { AdGroupsFilterImprovementEnabled } = Permissions.dynamic;

  if (gridNameSource && AdGroupsFilterImprovementEnabled) {
    existingDataSource = DataSourceStore.get(gridNameSource);

    if (existingDataSource) {
      // eslint-disable-next-line prefer-destructuring
      dataSource = existingDataSource.dataSource;
      // eslint-disable-next-line prefer-destructuring
      filterService = existingDataSource.filterService;
      // eslint-disable-next-line prefer-destructuring
      filterConfig = existingDataSource.filterConfig;
    }
  }
  if (!gridNameSource || !AdGroupsFilterImprovementEnabled || !existingDataSource) {
    filterConfig = getFilterConfig({
      ...restParams,
      edm,
      i18n,
      scope,
      jsdata,
      appConfig,
      cacheService,
      entitySchema: AdGroup,
      sessionStore,
      preferenceService,
      customColumnPreference,
      preferenceStoreFactory: preferenceStoreFactory.getFilters,
      dataContextManager,
    });
    filterService = new FilterService(filterConfig);
  }

  const columnConfig = getColumnConfig({ gridName, scope: scopeAdapter, preferenceService });

  return {
    existingDataSource,
    dataSource,
    filterService,
    filterConfig,
    queryBuilder: {
      decorators: _.compact([
        {
          ...columnConfig,
          name: 'columnChooser',
          customColumnsEnabled: true,
          addPoPColumnsOnComparisonEnabled: true,
          IsAvgPositionDeprecationPhase2Enabled: Permissions.IsAvgPositionDeprecationPhase2Enabled,
          comparisonPoPColumns: [
            'PeriodPerformanceMetrics',
            'PerformanceMetricsChange',
            'PerformanceMetricsChangePercentage',
          ],
        },
        { name: 'dateFilter' },
        { name: 'segment' },
        { name: 'pagination', defaultPageSize: 50 },
        { name: 'sorting' },
        { name: 'filter', filterService },
        { name: 'gridDataParameters', entitySet },
        AdgroupsCombineGridAndChartOdataCallEnabled && {
          name: 'chartDataParameters',
          entitySet,
          pageState: state,
          pageContext: { ...pageContext, CurrentTabId }, // At this point pageContext.CurrentTabId is still undefined, so we manually set the pageContext.CurrentTabId to 'Adgroups'
          chartDataAdapter: new ChartDataAdapter({ i18n, permissions: Permissions }),
          inlineChartPreference: new InlineChartPreferences(
            store,
            appConfig,
            adapter,
            legacyPreferenceService.initialize().then(() => Promise.promisifyAll(legacyPreferenceService))
          ),
        },
      ]),
      commonConfig: {
        featureName: gridName,
        isOdataActionSource,
        appConfig,
        appState: state,
        deps: {
          dateUtil,
          state,
          ColumnChooserPreference,
          CustomColumnPreference: customColumnPreference,
          SegmentationPreference,
          fetchPaginationConfig,
          fetchSortInfo,
        },
        IsColumnConversionEnabled,
        ...IsColumnConversionEnabled ? {
          columnConversions: getColumnsConversionMap(gridName, Permissions),
        } : {},
      },
      logActivity,
      initialQuery,
    },
  };
}
